import React, { useCallback, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Dialog, H1, HStack, IconLink, Link, Text, VStack } from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';
import { useNavigate, useParams } from 'react-router-dom';

import { EditSecuritiesLendingCard } from './EditSecuritiesLendingCard';
import { HouseholdPortfolioControlsDocument } from './graphql/query.householdPortfolioControls.generated';

import { WorkflowPage } from '../../../Shared/Components/WorkflowPage';
import urlBuilder from '../../../Shared/url-builder';

export const EditSecuritiesLending: React.FC = () => {
  const navigate = useNavigate();
  const { householdId } = useParams<{ householdId: string }>();
  const [inviteMemberId, setInviteMemberId] = useState<string | null>(null);
  const handleOpenInviteModal = useCallback((memberId: string) => () => setInviteMemberId(memberId), []);
  const handleCloseInviteModal = useCallback(() => setInviteMemberId(null), []);

  const { data } = useGraphQLQuery(HouseholdPortfolioControlsDocument, {
    variables: { id: householdId! },
  });

  if (!data) {
    return <CardLoader />;
  }

  const { members } = data.household;

  if (members.length === 0) {
    return null;
  }

  const handleCancel = () => {
    void navigate(urlBuilder.getHouseholdsSettingsPage.absolute({ householdId: householdId! }));
  };

  return (
    <WorkflowPage>
      <WorkflowPage.Header>
        <Box />
        <Box css={{ display: 'flex', justifyContent: 'center', maxWidth: '600px', margin: '0 auto' }}>
          <Text variant="text50">Manage securities lending</Text>
        </Box>
        <Box css={{ justifySelf: 'flex-end' }}>
          <IconLink
            label="Cancel"
            name="X"
            spacing="compact"
            variant="ghostSecondary"
            role="button"
            onClick={handleCancel}
          />
        </Box>
      </WorkflowPage.Header>

      <WorkflowPage.Main>
        <Box css={{ maxWidth: '600px', margin: '0 auto', padding: 'space.400' }}>
          <VStack css={{ gap: 'space.400' }}>
            <VStack css={{ gap: 'space.200' }}>
              <H1 variant="title300">Manage securities lending</H1>
              <Text variant="text100" css={{ color: 'text.weak' }}>
                Securities lending is coming soon. Clients will receive an invite email to confirm their enrollment.
              </Text>
              <Text variant="text100" css={{ color: 'text.weak' }}>
                For joint accounts, settings apply if enabled for either member. You can update these settings at any
                time.
              </Text>
            </VStack>

            <VStack css={{ gap: 'space.300' }}>
              {members.map(member => (
                <EditSecuritiesLendingCard
                  key={member.id}
                  member={member}
                  handleOpenInviteModal={handleOpenInviteModal}
                />
              ))}
            </VStack>

            <Text>
              By enrolling clients in securities lending, you acknowledge your understanding of you client's financial
              profile and that securities lending is suitable for their accounts. By opting into securities lending, you
              attest that you and your client have read and your client agrees to the{' '}
              <Link target="_blank" href={urlBuilder.getSecuritiesLendingAgreement()}>
                Apex Master Securities Lending Agreement
              </Link>{' '}
              and the{' '}
              <Link target="_blank" href={urlBuilder.getSecuritiesLendingDisclosure()}>
                Important Disclosures
              </Link>
              . Your client will be automatically enrolled once securities lending becomes available. Apex is the sole
              borrower of securities under the Fully-Paid Securities Lending (“FPSL”) Program and may lend out their
              securities. Loaned securities are not covered by SIPC, but are backed by at least 100% cash collateral
              held at a third-party bank. To learn more, please review our FAQs or reach out to support.
            </Text>
          </VStack>
        </Box>
      </WorkflowPage.Main>

      <Dialog
        open={inviteMemberId !== null}
        overlay="default"
        onOpenChange={open => {
          if (!open) handleCloseInviteModal();
        }}
      >
        <Dialog.Content
          size="large"
          css={{ display: 'flex', flexDirection: 'column', gap: 'space.300', p: 'space.400' }}
        >
          <Dialog.Title asChild={true}>
            <H1 variant="title300">
              Invite {members?.find(m => m.id === inviteMemberId)?.commonFirstName ?? 'this client'} to enable
              securities lending.
            </H1>
          </Dialog.Title>
          <Text variant="text100">
            Your client will receive an invite via email. They will need to confirm opt in before securities lending is
            turned on for their accounts.
          </Text>
          <HStack css={{ gap: 'space.200', justifyContent: 'flex-start' }}>
            <Button variant="solidPrimary" onClick={handleCloseInviteModal}>
              Send invite
            </Button>
          </HStack>
        </Dialog.Content>
      </Dialog>
    </WorkflowPage>
  );
};
